<script setup>
import { ref, computed, watch } from 'vue';
import { useRoute } from 'vue-router';
import { useAuth } from '/app/composables/auth';
import { useResponsive } from '/app/composables/responsive';
import AdminSidebar from './AdminSidebar.vue';
import GenericSidebar from './GenericSidebar.vue';
import AccountSidebar from './AccountSidebar.vue';
import DashboardSidebar from './DashboardSidebar.vue';
import FaqSidebar from './FaqSidebar.vue';
import NIcon from '/app/components/NIcon.vue';

const route = useRoute();
const { auth } = useAuth();
const responsive = useResponsive();

const collapsed = ref(responsive.sm);

const sidebar = computed(() => {
    switch (route.name && route.name.split('.')[0]) {
        case 'dashboard':
            return DashboardSidebar;
        case 'account':
            return AccountSidebar;
        case 'faqs':
            return FaqSidebar;
        case 'admin':
            return AdminSidebar;
        default:
            return GenericSidebar;
    }
});

const dashboard = computed(() => {
    // NOTE: switch on the component object identity, not strings — `sidebar.value`
    // is the imported SFC (e.g. `GenericSidebar`), not its name. The previous
    // string-comparison version always fell through to `default`, which made
    // every page render the My Dashboard link with a missing :platformSlug param.
    switch (sidebar.value) {
        case GenericSidebar:
            return {
                title: 'About',
                route: { name: 'about' },
            };
        case AccountSidebar:
            return {
                title: 'My Account',
                route: { name: 'account.my-account' },
            };
        case FaqSidebar:
            return {
                title: 'FAQs',
                route: { name: 'faqs' },
            };
        case AdminSidebar:
            return {
                title: 'Admin Dashboards',
                route: { name: 'admin.dashboards' },
            };
        default: {
            // dashboard.dashboard is nested under `/:platformSlug/my-dashboard`
            // (required param). Fall back to 'me' so the route's beforeEnter can
            // resolve it to the real screen_name once auth hydrates.
            const platformSlug = auth.user?.screen_name || route.params.platformSlug || 'me';
            return {
                title: auth.authenticated ? 'My Dashboard' : 'Dashboard',
                route: {
                    name: 'dashboard.dashboard',
                    params: { platformSlug },
                },
            };
        }
    }
});

function toggle() {
    collapsed.value = !collapsed.value;
}

watch(() => responsive.sm, (state) => {
    collapsed.value = state;
});

watch(() => route.fullPath, () => {
    if (responsive.sm) {
        collapsed.value = true;
    }
});
</script>

<template>
    <aside class="page__sidebar sm:min-w-[12rem]" :class="{ 'is-collapsed': collapsed }">
        <button aria-label="button" v-if="responsive.sm" type="button" class="tracking-wider shadow border-2 px-6 text-white flex items-center justify-between py-1 border-nerve bg-nerve rounded-md w-full" @click.prevent="toggle">
            <span class="flex items-center whitespace-nowrap">
                <span class="uppercase">{{ dashboard.title }}</span>
                <small class="italic ml-2">(Open the menu)</small>
            </span>
            <NIcon :as="collapsed ? 'caret-square-down' : 'caret-square-up'" class="ml-2" />
        </button>
        <div v-else class="page__sidebar-title">
            <router-link :to="dashboard.route">
                {{ dashboard.title }}
            </router-link>
            <button aria-label="button" type="button" @click.prevent="toggle">
                <NIcon :as="collapsed ? 'caret-square-down' : 'caret-square-up'" />
            </button>
        </div>
        <component :is="sidebar"></component>
    </aside>
</template>
